import Colors from '../../library/Colors';

/**
 * Formatting and labels for the Tijdsregistratie (Prikklok 2027) screens.
 * Same wording as the MyFasMan Mobile time clock module.
 */

const pad = (n) => String(n).padStart(2, '0');

/** Minutes as hours and minutes: 8u41 (nl), 8h41 (fr), 8:41 (en). */
// The time clock tables (time_clock_*) arrive with database version 320;
// on an older database the module can not be used or switched on.
export const TIMECLOCK_MIN_DB_VERSION = 320;

export const hm = (minutes, lang) => {
    if (minutes === null || minutes === undefined || isNaN(minutes)) return '—';
    const value = Math.abs(Math.round(minutes));
    const sep = lang === 'nl' ? 'u' : lang === 'fr' ? 'h' : ':';
    return (minutes < 0 ? '−' : '') + Math.floor(value / 60) + sep + pad(value % 60);
};

/** Signed difference: +0u08 / −6u38. */
export const hmSigned = (minutes, lang) => {
    if (minutes === null || minutes === undefined) return '—';
    return (minutes > 0 ? '+' : '') + hm(minutes, lang);
};

/** Hours with two decimals, as payroll uses them. */
export const hoursDecimal = (minutes) => (minutes / 60).toFixed(2);

/** "2026-09-24 08:52:14" -> "08:52" */
export const time = (dateTime) => (dateTime && dateTime.length >= 16 ? dateTime.substring(11, 16) : '');

/** Local date as Y-m-d. */
export const isoDate = (date) => date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());

export const parseDate = (text) => {
    if (!text || text.length < 10) return null;
    const [y, m, d] = text.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
};

export const addDays = (text, days) => {
    const date = parseDate(text);
    date.setDate(date.getDate() + days);
    return isoDate(date);
};

/** Monday of the week of a date (Y-m-d). */
export const mondayOf = (text) => {
    const date = parseDate(text) || new Date();
    const offset = (date.getDay() + 6) % 7;
    date.setDate(date.getDate() - offset);
    return isoDate(date);
};

/** ISO week number. */
export const isoWeek = (text) => {
    const date = parseDate(text);
    const target = new Date(date.valueOf());
    target.setDate(target.getDate() - ((date.getDay() + 6) % 7) + 3);
    const firstThursday = new Date(target.getFullYear(), 0, 4, 12);
    return 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
};

const locale = (lang) => (lang === 'fr' ? 'fr-BE' : lang === 'en' ? 'en-GB' : 'nl-BE');

/** "ma 14/09" */
export const dayLabel = (text, lang) => {
    const date = parseDate(text);
    if (!date) return text || '';
    const weekday = date.toLocaleDateString(locale(lang), { weekday: 'short' }).replace('.', '');
    return weekday + ' ' + pad(date.getDate()) + '/' + pad(date.getMonth() + 1);
};

/** Short weekday only: "ma" */
export const weekdayShort = (text, lang) => {
    const date = parseDate(text);
    return date ? date.toLocaleDateString(locale(lang), { weekday: 'short' }).replace('.', '') : '';
};

/** "24/09/2026" */
export const dateLabel = (text) => {
    const date = parseDate(text);
    return date ? pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear() : '';
};

/** "september 2026" */
export const monthLabel = (text, lang) => {
    const date = parseDate(text);
    return date ? date.toLocaleDateString(locale(lang), { month: 'long', year: 'numeric' }) : '';
};

export const firstOfMonth = (text) => text.substring(0, 8) + '01';

export const lastOfMonth = (text) => {
    const date = parseDate(text);
    return isoDate(new Date(date.getFullYear(), date.getMonth() + 1, 0, 12));
};

/** Now as "Y-m-dTH:i" for a datetime-local input. */
export const nowLocalInput = () => {
    const d = new Date();
    return isoDate(d) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
};

export const REASON_CODES = ['FORGOT_IN', 'FORGOT_OUT', 'FORGOT_BREAK', 'WRONG_TIME', 'WRONG_TYPE',
    'WRONG_PERSON', 'DUPLICATE', 'SYSTEM', 'OTHER'];

export const EVENT_TYPES = ['IN', 'OUT', 'BREAK_START', 'BREAK_END'];

export const typeLabel = (t, type) => ({
    IN: t('tc_type_in', 'In'),
    OUT: t('tc_type_out', 'Out'),
    BREAK_START: t('tc_type_break_start', 'Break'),
    BREAK_END: t('tc_type_break_end', 'End of break'),
}[type] || type || '');

export const reasonLabel = (t, code) => ({
    FORGOT_IN: t('tc_reason_forgot_in', 'Forgot to clock in'),
    FORGOT_OUT: t('tc_reason_forgot_out', 'Forgot to clock out'),
    FORGOT_BREAK: t('tc_reason_forgot_break', 'Break not stamped'),
    WRONG_TIME: t('tc_reason_wrong_time', 'Wrong time'),
    WRONG_TYPE: t('tc_reason_wrong_type', 'Wrong action'),
    WRONG_PERSON: t('tc_reason_wrong_person', 'Wrong person'),
    DUPLICATE: t('tc_reason_duplicate', 'Duplicate stamp'),
    SYSTEM: t('tc_reason_system', 'Malfunction'),
    OTHER: t('tc_reason_other', 'Other'),
}[code] || code || '');

export const stateLabel = (t, state) => ({
    WORKING: t('tc_state_in', 'Clocked in'),
    ON_BREAK: t('tc_state_break', 'On break'),
    OFF: t('tc_state_out', 'Clocked out'),
}[state] || state || '');

export const sourceLabel = (t, source) => ({
    POS: t('tc_source_pos', 'Till'),
    OFFICE: 'MyFashionManager',
    ANDROID: 'Android',
    IOS: 'iOS',
    WEB: 'MySoftTouch',
    IMPORT: t('tc_source_import', 'Import'),
    LEGACY: t('tc_source_legacy', 'Old till'),
}[source] || source || '');

export const dayStatusLabel = (t, status) => ({
    CLOSED: t('tc_status_closed', 'OK'),
    CORRECTED: t('tc_status_corrected', 'Corrected'),
    INCOMPLETE: t('tc_status_incomplete', 'Incomplete'),
    OPEN: t('tc_status_open', 'Running'),
}[status] || status || '');

/**
 * Surfaces on the page panel (.light #F9FAFB / .dark #1B212B), from the app
 * palette: white cards on light, dark_accent cards on dark. Text colours come
 * from the MUI theme ('text.secondary'), which follows light/dark.
 */
export const surface = (theme) => {
    const dark = theme === 'dark';
    return {
        dark,
        card: {
            backgroundColor: dark ? Colors.dark_accent : '#FFFFFF',
            border: '1px solid ' + (dark ? 'rgba(255,255,255,0.08)' : Colors.light_accent_darker),
            borderRadius: '10px',
        },
        line: '1px solid ' + (dark ? 'rgba(255,255,255,0.07)' : Colors.light_accent),
        hover: dark ? 'rgba(255,255,255,0.06)' : Colors.light_accent,
        faint: dark ? '#9CA3AF' : '#6B7280',
        ghost: dark ? '#4B5563' : Colors.light_accent_darkest,
    };
};

/** Semantic colours; kept apart from the brand blue. */
export const tone = {
    ok: Colors.def_green,
    warn: '#B7791F',
    bad: Colors.def_red,
    info: Colors.sft_blue,
    muted: '#8B93A1',
};

export const dayStatusTone = (status) => ({
    CLOSED: tone.ok, CORRECTED: tone.info, INCOMPLETE: tone.bad, OPEN: tone.muted,
}[status] || tone.muted);

export const stateTone = (state) => ({
    WORKING: tone.ok, ON_BREAK: tone.warn, OFF: tone.muted,
}[state] || tone.muted);

/** Readable message from a SftGlobalData.post failure. */
export const errorText = (t, response) => {
    const key = response?.translation_key || response?.data?.translation_key;
    const known = {
        timeclock_not_manager: t('tc_err_not_manager', 'Only a time clock manager (Administrator group or appointed manager) can open this page.'),
        timeclock_db_update: t('tc_err_db_update', 'This database does not have the time clock update yet.'),
        timeclock_no_api: t('tc_err_no_api', 'This database has no API account.'),
        timeclock_api_missing: t('tc_err_api_missing', 'The time clock service is not available on the API yet.'),
        timeclock_api_unreachable: t('tc_err_unreachable', 'The time clock service cannot be reached. Try again.'),
        timeclock_reason_required: t('tc_err_reason', 'Give an explanation.'),
        timeclock_future: t('tc_err_future', 'A stamp cannot be in the future.'),
        timeclock_invalid_value: t('tc_err_value', 'This value is not allowed.'),
        timeclock_invalid_position: t('tc_err_position', 'Give the position as latitude, longitude, for example 51.054340,3.717420.'),
        timeclock_no_address: t('tc_err_no_address', 'This store has no address. Use your current location or fill in the position.'),
        timeclock_address_not_found: t('tc_err_address_not_found', 'The address was not found. Use your current location or fill in the position.'),
    };
    if (key && known[key]) return known[key];
    return response?.message || response?.data?.message || t('tc_err_generic', 'Something went wrong.');
};
